import {
  Children,
  cloneElement,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from 'react';

import { Route, Routes, useLocation, useResolvedPath } from 'react-router-dom';
import { Tabs, TabsProps, Box, Stack } from '@cast/design-system';

import { Navigate } from 'components/router';

import { RoutedTab, RoutedTabProps } from './RoutedTab';

import { RoutedTabContent } from './RoutedTabContent';
import type { StackProps } from '@cast/design-system';

export const generateTabPath = (
  path: string,
  tabPath: string,
  search: string
) => {
  const parts = path.split('/');
  if (parts.length > 0) {
    parts.pop(); // Need to exclude tabs segments from current path
  }
  return parts.join('/') + '/' + tabPath + search;
};

type UnknownTabRedirectProps = {
  firstTabValue: string;
  preserveSearch: boolean;
};

const UnknownTabRedirect = ({
  firstTabValue,
  preserveSearch,
}: UnknownTabRedirectProps) => {
  const { pathname: firstTabPath } = useResolvedPath(firstTabValue);

  return <Navigate to={firstTabPath} replace preserveSearch={preserveSearch} />;
};

type Props = PropsWithChildren<TabsProps> & {
  headerContent?: ReactNode;
  tabsContainerProps?: StackProps;
  wrapContentWith?: (content: ReactNode) => ReactNode;
  preserveSearchParams?: boolean;
  /**
   * Redirects unknown tab paths to the first tab. Opt-in, because tabs are
   * often registered conditionally (e.g. once a permission or query resolves),
   * and a deep link to such a tab would get redirected away before it mounts.
   * Only enable it when the whole tab set is known on the first render.
   */
  redirectUnknownTabToFirst?: boolean;
};

export const RoutedTabs = ({
  children,
  headerContent,
  tabsContainerProps,
  wrapContentWith,
  preserveSearchParams = false,
  redirectUnknownTabToFirst = false,
  ...props
}: Props) => {
  const { pathname, search } = useLocation();

  const tabs: ReactElement[] = [];
  const tabContent: any[] = [];
  let currentTab: string | undefined;
  Children.forEach(children, (t) => {
    if (isValidElement<RoutedTabProps>(t)) {
      if (t.type === RoutedTab) {
        const value = t.props.value;
        const searchParams = preserveSearchParams ? search : '';
        const tabPathname = generateTabPath(pathname, t.props.value, '');
        const tabPath = generateTabPath(pathname, t.props.value, searchParams);
        tabs.push(
          cloneElement(t, {
            key: value,
            value,
            to: tabPath,
          } as RoutedTabProps)
        );
        if (pathname === tabPathname) {
          currentTab = value;
        }
      } else if (t.type === RoutedTabContent) {
        tabContent.push({ value: t.props.value, children: t.props.children });
      }
    }
  });

  const renderChildren = (children: ReactNode) => {
    if (wrapContentWith) {
      return wrapContentWith(children);
    }
    return children;
  };

  return (
    <>
      {currentTab && (
        <Stack direction="row" width="100%" {...tabsContainerProps}>
          <Box flexGrow={1}>
            <Tabs value={currentTab} {...props}>
              {tabs}
            </Tabs>
          </Box>
          {headerContent}
        </Stack>
      )}
      <Routes>
        <Route
          index
          element={
            <Navigate
              to={tabContent[0]?.value}
              preserveSearch={preserveSearchParams}
            />
          }
        />
        {tabContent.map((c) => {
          return (
            <Route
              key={c.value}
              path={c.value}
              element={renderChildren(c.children)}
            />
          );
        })}
        {redirectUnknownTabToFirst && tabContent.length > 0 && (
          <Route
            path="*"
            element={
              <UnknownTabRedirect
                firstTabValue={tabContent[0].value}
                preserveSearch={preserveSearchParams}
              />
            }
          />
        )}
      </Routes>
    </>
  );
};
